/**
 * Share-intent URL builders + progressive-enhancement helpers — TS-016 (FR-4.5/4.6/4.8).
 *
 * Implements TS-016 (T2). Pure functions only: no React, no DOM reads beyond
 * feature-detection, no third-party social SDK, no tracking script. Every
 * builder returns a plain https share-intent URL that opens in a new tab via
 * a plain <a target="_blank"> — this is the ONLY sharing mechanism required
 * by BR-TS016.1 (no SDK/JS loader for WhatsApp/Telegram/Facebook).
 *
 * KB TO-LL-036 (fail-safe error handling — never swallow a real failure):
 * copyToClipboard's fallback path exists specifically so an unavailable
 * Clipboard API degrades gracefully (edge case: "Clipboard API unavailable")
 * instead of throwing/crashing the UI. This is a documented UX fallback, not
 * a swallow of a genuine error — callers still get a boolean success signal.
 */

/**
 * Build the WhatsApp share-intent URL.
 *
 * NOTE: uses api.whatsapp.com/send (NOT bare wa.me) per AC-TS016.3 — this is
 * the officially documented WhatsApp click-to-chat/share endpoint and is the
 * exact host the acceptance criterion requires.
 *
 * Implements TS-016 (T2); enforces AC-TS016.3 (WhatsApp carries the prefilled
 * message; no SDK).
 */
export function whatsappShareUrl(message: string): string {
  return `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
}

/**
 * Build the Telegram share-intent URL. Telegram's share endpoint accepts a
 * canonical `url` (unfurled as a link preview) plus a `text` caption.
 *
 * Implements TS-016 (T2); enforces AC-TS016.3 (Telegram carries the
 * prefilled message; no SDK).
 */
export function telegramShareUrl(link: string, message: string): string {
  return `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(message)}`;
}

/**
 * Build the Facebook share-intent URL.
 *
 * Facebook's sharer only accepts the `u=` canonical link param — it ignores
 * any prefilled text by design (Facebook strips custom OG-bypassing text to
 * prevent spam), so no `text`/`quote` param is offered here.
 *
 * Implements TS-016 (T2); enforces AC-TS016.3 (no SDK, correct intent URL).
 */
export function facebookShareUrl(link: string): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`;
}

/**
 * Feature-detect the Web Share API (progressive enhancement only — AC-TS016.4).
 * The explicit WhatsApp/Telegram/Facebook + Copy buttons are the universal
 * path; Web Share is an ADDITIONAL button shown only when supported.
 *
 * Implements TS-016 (T2).
 */
export function canWebShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/**
 * Invoke the native Web Share sheet. Callers must catch AbortError (user
 * cancelled the share sheet) and treat it as a no-op — that is documented,
 * expected UX, not an application error.
 *
 * Implements TS-016 (T2); enforces AC-TS016.4 and AC-TS016.5 (Instagram is
 * reached via this native sheet, never a fake one-tap web-post URL).
 */
export async function webShare(data: { title?: string; text: string; url: string }): Promise<void> {
  await navigator.share(data);
}

/**
 * Copy text to the clipboard. Primary path uses the async Clipboard API
 * (requires HTTPS + user gesture); falls back to a hidden textarea +
 * document.execCommand('copy') so an unavailable/denied Clipboard API never
 * crashes the UI (edge case: "Clipboard API unavailable").
 *
 * Implements TS-016 (T2); enforces edge case 2 and KB TO-LL-036 (fallback
 * degrades gracefully rather than throwing — this is a UX fallback, not a
 * silent swallow of a genuine failure: callers get an explicit boolean).
 *
 * @returns true if the copy succeeded via either path, false otherwise.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the execCommand fallback below.
    }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
